import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ExcelService } from '../../components/shared/services/excel.service';
import { parseMonetary } from '../../components/shared/services/price-parser';
import { isProduct } from '../models/item-type';
import {
  CategoryDTO,
  SupplierDTO,
  UnitOfMeasureDTO,
  EnhancedItemDTO,
} from '../../components/UI/inventory-dashboard/inventory-dashboard.component';
import { InventoryDataService, ItemTypeDTO } from './inventory-data.service';

/**
 * Reference data the import pipeline needs to resolve/validate rows against.
 * Callers (Products page, Raw Materials page, or today's shared dashboard)
 * assemble this from whatever lists they already hold — the service caches
 * nothing itself, since two pages will have two different item pools.
 */
export interface ImportContext {
  categories: CategoryDTO[];
  suppliers: SupplierDTO[];
  uomList: UnitOfMeasureDTO[];
  itemTypes: ItemTypeDTO[];
  /** Existing items (across whichever pages/pools the caller cares about) used for duplicate-itemCode detection. */
  existingItems: EnhancedItemDTO[];
}

export interface ImportRowError {
  item: any;
  error: string;
  severity: 'error' | 'warning';
  rowIndex: number;
  rowNumber: number;
  rawValue?: string;
  itemName?: string;
}

export interface ImportValidationResult {
  errors: ImportRowError[];
  warnings: ImportRowError[];
}

export interface ImportRunResult {
  success: number;
  /** Combined validation errors, validation warnings, and per-row save errors — matches the component's previous importResults.errors shape. */
  errors: ImportRowError[];
}

/**
 * Excel import pipeline for inventory items, extracted out of
 * InventoryDashboardComponent. Branches internally on item type (raw
 * material vs finished good/service) so it can serve both the future
 * Products and Raw Materials pages from one place. Owns no page/component
 * state — everything it needs (categories, suppliers, UOMs, item types,
 * existing items for dedup) is passed in via ImportContext.
 */
@Injectable({ providedIn: 'root' })
export class InventoryImportService {
  constructor(
    private http: HttpClient,
    private excelService: ExcelService,
    private inventoryDataService: InventoryDataService,
  ) {}

  private authHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  /**
   * Excel column header (normalized, lowercased, whitespace-stripped) to the
   * field name used throughout the import pipeline. Shared by the validate
   * pass and the POST pass so they cannot drift apart again.
   */
  private readonly importFieldMappings: { [key: string]: string } = {
    itemname: 'itemName',
    category: 'category',
    unitofmeasure: 'unitOfMeasure',
    uom: 'unitOfMeasure',
    purchaseprice: 'purchasePrice',
    unitcost: 'unitCost',
    purchasequantity: 'purchaseQuantity',
    lowstockthreshold: 'lowStockThreshold',
    sellingprice: 'sellingPrice',
    sku: 'itemCode',
    producttype: 'productType'
  };

  /** Map an already-normalized row (lowercased/camelCased keys) onto the field names used by the importer. */
  private mapImportRow(normalizedRow: any): any {
    const mappedRow: any = {};
    Object.keys(normalizedRow).forEach(key => {
      const mappedKey = this.importFieldMappings[key.toLowerCase()] || key;
      mappedRow[mappedKey] = normalizedRow[key];
    });
    return mappedRow;
  }

  /** Normalize raw Excel row keys: strip trailing asterisks, lowerCamelCase, blank -> null. */
  private normalizeImportRow(row: any): any {
    const normalizedRow: any = {};
    Object.keys(row).forEach(key => {
      const normalizedKey = key
        .replace(/\*+$/, '')
        .toLowerCase()
        .replace(/([-_][a-z])/g, group => group.toUpperCase().replace(/[-_]/g, ''));
      const value = row[key];
      normalizedRow[normalizedKey] = (value === '' || value === undefined) ? null : value;
    });
    return normalizedRow;
  }

  /** True for the instructional/example rows baked into the templates — these should be skipped, not validated. */
  private isInstructionRow(normalizedRow: any): boolean {
    const itemName = String(normalizedRow.itemName || '').trim();
    const lowerItemName = itemName.toLowerCase();
    return (
      lowerItemName === '' ||
      lowerItemName === 'example item' ||
      lowerItemName.startsWith('* required fields') ||
      lowerItemName.startsWith('instructions:') ||
      lowerItemName.startsWith('1. fill in the required fields') ||
      lowerItemName.startsWith('2. select values from dropdowns') ||
      lowerItemName.startsWith('3. do not modify the header') ||
      lowerItemName.startsWith('4. delete the example row') ||
      lowerItemName.startsWith('5. save as .xlsx file') ||
      lowerItemName.includes('example row') ||
      lowerItemName.includes('instruction')
    ) && (
        // Only skip if other fields that would indicate real data are also empty
        !normalizedRow.category &&
        !normalizedRow.unitofmeasure &&
        !normalizedRow.purchaseprice &&
        !normalizedRow.producttype
      );
  }

  /**
   * Determine the item type for an import row. The "Product Type" column
   * (mappedRow.productType) is authoritative when it resolves against the
   * live itemTypes list — otherwise fall back to the legacy heuristic for
   * old-format sheets/rows with no Product Type value. A blank Product Type
   * on the product template defaults to Finished Good.
   */
  private resolveImportItemType(mappedRow: any, normalizedRow: any, itemTypes: ItemTypeDTO[]): { isFinishedGood: boolean; itemTypeID: number } {
    const productTypeRaw = String(mappedRow.productType || '').trim();
    if (productTypeRaw) {
      const matchedType = itemTypes.find(t => t.typeName?.toLowerCase() === productTypeRaw.toLowerCase());
      if (matchedType) {
        return { isFinishedGood: isProduct({ itemTypeID: matchedType.itemTypeID }), itemTypeID: matchedType.itemTypeID };
      }
    }

    if (String(normalizedRow.itemtypeid || '') === '2') {
      return { isFinishedGood: true, itemTypeID: 2 };
    }

    // Legacy heuristic fallback (no Product Type column / value provided)
    const isFinishedGood = (
      (mappedRow.sellingPrice !== undefined && mappedRow.sellingPrice !== null) &&
      (mappedRow.purchasePrice === undefined && mappedRow.purchasePrice === null) &&
      (mappedRow.purchaseQuantity === undefined && mappedRow.purchaseQuantity === null)
    ) || String(normalizedRow.itemtype || '').toLowerCase() === 'finished';

    return { isFinishedGood, itemTypeID: isFinishedGood ? 2 : 1 };
  }

  /** Required fields differ for finished goods (products) vs raw materials. Selling Price is optional for both. */
  private importRequiredFields(isFinishedGood: boolean): string[] {
    return isFinishedGood
      ? ['itemName', 'category', 'unitOfMeasure']
      : ['itemName', 'category', 'unitOfMeasure', 'purchasePrice', 'purchaseQuantity'];
  }

  /**
   * Sanitise the monetary fields on a mapped import row in place.
   *
   * Unrecoverable values (em dash, arbitrary text, negatives) throw, which the
   * caller turns into a row rejection. Values that had to be corrected, and
   * explicit zeroes, are returned as warnings so they can be reported for
   * review without blocking the import.
   *
   * Shared by validateImportItems (pre-flight) and processBatch (the function
   * that actually POSTs) so the two paths cannot drift apart again.
   */
  private sanitiseRowPrices(mappedRow: any): string[] {
    const priceFields = ['purchasePrice', 'sellingPrice', 'unitCost'];
    const warnings: string[] = [];

    priceFields.forEach(field => {
      const raw = mappedRow[field];
      if (raw === undefined || raw === null || raw === '') {
        return;
      }

      const { value, error, sanitised, rawValue } = parseMonetary(raw);
      if (error) {
        throw new Error(`${field}: ${error}`);
      }

      if (sanitised) {
        warnings.push(`${field} was corrected from "${rawValue}" to ${value}`);
      } else if (value === 0) {
        warnings.push(`${field} is 0 — verify this is intentional`);
      }

      mappedRow[field] = value;
    });

    return warnings;
  }

  /** Validate the import rows without saving anything. Returns row-level errors and warnings. */
  validateImportItems(items: any[], context: ImportContext): ImportValidationResult {
    const errors: ImportRowError[] = [];
    const warnings: ImportRowError[] = [];
    // Track duplicates by itemCode (case-insensitive)
    const seenCodes = new Set<string>();
    const existingCodes = new Set(
      (context.existingItems || [])
        .map(i => (i?.itemCode || '').toLowerCase())
        .filter(c => !!c)
    );

    items.forEach((row: any, index: number) => {
      try {
        // Normalize keys similar to processBatch
        const normalizedRow: any = this.normalizeImportRow(row);

        if (this.isInstructionRow(normalizedRow)) {
          return; // skip validation for instruction/example rows
        }

        const mappedRow: any = this.mapImportRow(normalizedRow);

        // Ensure SKU is available by auto-generating if missing (consistent with processBatch)
        mappedRow.itemCode = String(mappedRow.itemCode || this.inventoryDataService.generateSKU(mappedRow.itemName));

        // Clean and validate monetary values. Throws on unrecoverable values;
        // returns warnings for corrected values and explicit zeroes. These are
        // only recorded once the whole row validates, so a rejected row is not
        // also reported as needing review.
        const priceWarnings = this.sanitiseRowPrices(mappedRow);

        // Determine if this row represents a finished good (product). The
        // Product Type column is authoritative when present; otherwise fall
        // back to the legacy heuristic.
        const { isFinishedGood } = this.resolveImportItemType(mappedRow, normalizedRow, context.itemTypes);

        // Required fields differ for finished goods vs raw materials. Selling Price is optional.
        const requiredFields = this.importRequiredFields(isFinishedGood);
        const missingFields = requiredFields.filter(field => mappedRow[field] === undefined || mappedRow[field] === null || mappedRow[field] === '');
        if (missingFields.length > 0) {
          throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
        }

        // Duplicate checks (by itemCode)
        const codeKey = String((mappedRow.itemCode || '').toLowerCase());
        if (codeKey) {
          if (existingCodes.has(codeKey)) {
            throw new Error(`Duplicate item: itemCode '${mappedRow.itemCode}' already exists in system`);
          }
          if (seenCodes.has(codeKey)) {
            throw new Error(`Duplicate item in file: itemCode '${mappedRow.itemCode}' appears more than once`);
          }
          seenCodes.add(codeKey);
        }

        // Reference lookups
        const category = context.categories.find(c => c.categoryName?.toLowerCase() === String(mappedRow.category || '').toLowerCase());
        const uom = context.uomList.find(u =>
          u.uomName?.toLowerCase() === String(mappedRow.unitOfMeasure || '').toLowerCase() ||
          u.uomName?.toLowerCase() === String(normalizedRow.uom || '').toLowerCase() ||
          u.uomCode?.toLowerCase() === String(mappedRow.unitOfMeasure || '').toLowerCase() ||
          u.uomCode?.toLowerCase() === String(normalizedRow.uom || '').toLowerCase()
        );
        if (!category) {
          throw new Error(`Category not found: ${mappedRow.category}`);
        }
        if (!uom) {
          throw new Error(`Unit of measure not found: ${mappedRow.unitOfMeasure || normalizedRow.uom}`);
        }

        if (!isFinishedGood) {
          const supplier = normalizedRow.supplier
            ? context.suppliers.find(s => s.companyName?.toLowerCase() === String(normalizedRow.supplier || '').toLowerCase())
            : null;
          if (!supplier) {
            throw new Error('Supplier is required for raw materials');
          }
        }

        // The row is valid; now surface any price corrections for review.
        priceWarnings.forEach(warning => {
          warnings.push({
            item: row,
            error: warning,
            severity: 'warning',
            rowIndex: index + 1,
            rowNumber: row.__excelRow ?? (index + 1),
            rawValue: String(row['purchaseprice*'] ?? row['purchaseprice'] ?? '')
          });
        });

      } catch (err: any) {
        errors.push({
          item: row,
          error: err?.message || 'Unknown validation error',
          severity: 'error',
          rowIndex: index + 1,
          rowNumber: row.__excelRow ?? (index + 1),
          rawValue: String(row['purchaseprice*'] ?? row['purchaseprice'] ?? '')
        });
      }
    });

    return { errors, warnings };
  }

  /** POSTs a batch of already-validated rows. Used internally by processImportFile, in chunks of 10. */
  private async processBatch(batch: any[], context: ImportContext): Promise<ImportRunResult> {
    const results: ImportRunResult = { success: 0, errors: [] };
    const token = localStorage.getItem('authToken');

    if (!token) {
      throw new Error('Authentication required');
    }

    const headers = this.authHeaders();

    for (const [index, row] of batch.entries()) {
      try {
        // Normalize the row data by removing asterisks from property names and standardizing case
        const normalizedRow: any = this.normalizeImportRow(row);

        // Skip only rows that are clearly instructions or examples
        if (this.isInstructionRow(normalizedRow)) {
          continue;
        }

        // Find required references using normalized row data
        const category = context.categories.find(c =>
          c.categoryName?.toLowerCase() === normalizedRow.category?.toLowerCase()
        );

        const uom = context.uomList.find(u =>
          u.uomName?.toLowerCase() === normalizedRow.unitofmeasure?.toLowerCase() ||
          u.uomName?.toLowerCase() === normalizedRow.uom?.toLowerCase() ||
          u.uomCode?.toLowerCase() === normalizedRow.unitofmeasure?.toLowerCase() ||
          u.uomCode?.toLowerCase() === normalizedRow.uom?.toLowerCase()
        );
        if (!category) {
          throw new Error(`Category not found: ${normalizedRow.category}`);
        }
        if (!uom) {
          throw new Error(`Unit of measure not found: ${normalizedRow.unitofmeasure || normalizedRow.uom}`);
        }

        // Map Excel column names to our expected field names
        const mappedRow: any = this.mapImportRow(normalizedRow);

        // Ensure SKU is available by auto-generating if missing
        mappedRow.itemCode = String(mappedRow.itemCode || this.inventoryDataService.generateSKU(mappedRow.itemName));

        // Clean and validate monetary values. Re-run here (not just in
        // pre-flight) because this function re-derives the values it POSTs —
        // a row must never reach the API without passing through parseMonetary.
        this.sanitiseRowPrices(mappedRow);

        // Determine item type: the Product Type column is authoritative when
        // it resolves against the live itemTypes list; otherwise fall back to
        // the legacy heuristic for old-format sheets/rows.
        const { isFinishedGood, itemTypeID: resolvedItemTypeID } = this.resolveImportItemType(mappedRow, normalizedRow, context.itemTypes);

        // Validate required fields with proper field names. Selling Price is optional.
        const requiredFields = this.importRequiredFields(isFinishedGood);
        const missingFields = requiredFields.filter(field => {
          const value = mappedRow[field];
          return value === undefined || value === null || value === '';
        });

        if (missingFields.length > 0) {
          throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
        }

        if (!category || !uom) {
          throw new Error(`Invalid category or UOM for item: ${row.itemName}`);
        }

        // Prepare the item data matching saveItem structure.
        // mappedRow prices have already been through parseMonetary above, so
        // they are real numbers here. parseFloat is deliberately not used: it
        // is prefix-tolerant (parseFloat('12abc') === 12, parseFloat('1,500')
        // === 1) and would silently re-introduce partial values.
        const quantity = Number(mappedRow.purchaseQuantity ?? 0) || 0;
        const purchasePrice = Number(mappedRow.purchasePrice ?? 0) || 0;
        const unitCost = (() => {
          const provided = Number(mappedRow.unitCost ?? NaN);
          if (Number.isFinite(provided) && provided > 0) return provided;
          return quantity > 0 ? purchasePrice / quantity : 0;
        })();
        const sellingPrice = isFinishedGood ? (Number(mappedRow.sellingPrice ?? 0) || 0) : 0;
        const lowStockThreshold = parseInt(mappedRow.lowStockThreshold || '0', 10);
        // Prepare the item data with mapped field names
        const itemData: any = {
          ItemID: 0, // New item
          ItemName: mappedRow.itemName,
          ItemCode: mappedRow.itemCode || this.inventoryDataService.generateSKU(mappedRow.itemName),

          itemTypeID: resolvedItemTypeID,
          PurchaseUOMID: uom.uomid,
          lowStockThreshold: lowStockThreshold,
          isActive: true,
          valuationMethodCode: 'FIFO',
          categoryID: category.categoryId,
          profitMarginPercent: 0,
          quantity: quantity,
          quantityToAdd: quantity,
          purchasePrice: mappedRow.purchasePrice,
          unitCost: mappedRow.unitCost, // computed: unit cost = purchasePrice / purchaseQuantity when not provided
          sellingPrice: sellingPrice,
          // Ensure these are set based on item type
          purchaseQuantity: quantity, // Set purchase quantity to the imported quantity
          supplierID: !isFinishedGood ? (context.suppliers.find(s => s.companyName?.toLowerCase() === String(normalizedRow.supplier || '').toLowerCase())?.supplierID || 1) : 1
        };

        // Ensure we're using the correct case for the unit cost field
        itemData.purchasePrice = purchasePrice;      // total price
        itemData.unitCost = unitCost;                // per-unit
        itemData.purchaseQuantity = quantity;

        // Set prices based on item type
        if (isFinishedGood) {
          itemData.sellingPrice = sellingPrice;
          itemData.purchasePrice = 0;
          itemData.unitCost = 0; // Finished goods don't have unit cost
        } else {
          // For raw materials, require supplier
          if (!context.suppliers.find(s => s.companyName?.toLowerCase() === String(normalizedRow.supplier || '').toLowerCase())) {
            throw new Error('Supplier is required for raw materials');
          }
        }

        // Save the item first
        let response;
        try {
          response = await this.http.post(
            `${environment.apiUrl}/api/Items/Add`,
            itemData,
            { headers, observe: 'response' as const }
          ).toPromise();

          // Create inventory transaction if quantity was added
          if (itemData.quantityToAdd > 0) {
            const savedItem = response?.body as any;
            if (savedItem) {
              // Calculate transaction unit cost - prioritize explicit unitCost, else derive from purchasePrice/purchaseQuantity
              const transactionUnitCost = Number(itemData.unitCost) > 0
                ? Number(itemData.unitCost)
                : (Number(itemData.purchaseQuantity) > 0
                    ? Number(itemData.purchasePrice) / Number(itemData.purchaseQuantity)
                    : 0);
              const transactionData = {
                itemID: savedItem.itemID || savedItem.ItemID, // Handle different response formats
                warehouseID: 1,
                transactionType: 'Receipt',
                quantityChange: parseFloat(itemData.quantityToAdd) || 0,
                costPerUnit: transactionUnitCost,
                transactionDate: new Date(),
                referenceType: 'Import',
                referenceID: `IMP-${Date.now()}`,
                userID: 1,
                notes: 'Added via Excel Import',
                batchNumber: null,
                expiryDate: null,
                lowStockThreshold: itemData.lowStockThreshold || 0
              };
              // Create the inventory transaction
              await this.http.post(
                `${environment.apiUrl}/api/InventoryTransactions/Add`,
                transactionData,
                { headers }
              ).toPromise();

              // Update the item's unit cost if this is a raw material
              if (!isFinishedGood && transactionUnitCost > 0) {
                const updateData = {
                  ...savedItem,
                  unitCost: transactionUnitCost,
                  purchasePrice: Number(itemData.purchasePrice), // keep total purchase price; do not overwrite with unit cost
                  // Ensure we're not sending read-only fields that might cause issues
                  itemID: undefined,
                  ItemID: savedItem.itemID || savedItem.ItemID
                };
                try {
                  await this.http.put<any>(
                    `${environment.apiUrl}/api/Items/Update?ItemId=${savedItem.itemID || savedItem.ItemID}`,
                    updateData,
                    { headers, observe: 'response' as const }
                  ).toPromise();
                } catch (updateError) {
                  // Non-fatal: the item was created; the unit-cost refresh failed silently, as before.
                }
              }
            }
          }
        } catch (error) {
          throw error;
        }

        results.success++;
      } catch (error: any) {
        const errorMessage = error.error?.message || error.message || 'Unknown error';
        results.errors.push({
          item: row,
          itemName: row.itemName,
          rowIndex: index + 1,
          rowNumber: row.__excelRow ?? (index + 1),
          severity: 'error',
          error: errorMessage
        });
      }
    }

    return results;
  }

  /**
   * Reads the Excel file, validates every row, then POSTs the valid rows in
   * batches of 10, reporting progress after each batch. Returns the combined
   * result (validation errors + warnings + per-row save errors); throws only
   * when the file itself cannot be read/parsed, or when there is nothing
   * valid to import (callers check `validItemsFound` to distinguish that
   * case from a hard failure).
   */
  async processImportFile(
    file: File,
    context: ImportContext,
    onProgress?: (progress: number) => void
  ): Promise<ImportRunResult & { validItemsFound: boolean }> {
    const combined: ImportRunResult = { success: 0, errors: [] };

    const items = await this.excelService.readInventoryFile(file);
    const batchSize = 10;
    let processedItems = 0;

    // Pre-validate all rows. Collect errors but proceed with valid rows.
    const validation = this.validateImportItems(items, context);
    if (validation.errors.length > 0) {
      combined.errors.push(...validation.errors);
    }
    // Warnings (corrected values, explicit zeroes) are reported alongside
    // errors in the same modal but do not block the row from importing.
    if (validation.warnings.length > 0) {
      combined.errors.push(...validation.warnings);
    }
    const invalidRowIndexes = new Set((validation.errors || []).map(e => e.rowIndex));
    const validItems = items.filter((_, idx) => !invalidRowIndexes.has(idx + 1));

    if (validItems.length === 0) {
      return { ...combined, validItemsFound: false };
    }

    for (let i = 0; i < validItems.length; i += batchSize) {
      const batch = validItems.slice(i, i + batchSize);
      const results = await this.processBatch(batch, context);

      processedItems += batch.length;
      onProgress?.(Math.min(100, Math.round((processedItems / validItems.length) * 100)));
      combined.success += results.success;
      combined.errors.push(...results.errors);

      // Small delay to show progress
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return { ...combined, validItemsFound: true };
  }

  /** Rows that actually failed. Warnings imported fine and must not be re-exported. */
  getFailedRows(errors: ImportRowError[]): any[] {
    return (errors || []).filter(e => e.severity !== 'warning').map(e => e.item);
  }

  errorCount(errors: ImportRowError[]): number {
    return this.getFailedRows(errors).length;
  }

  warningCount(errors: ImportRowError[]): number {
    return (errors || []).filter(e => e.severity === 'warning').length;
  }

  /** Determine if an error row is a finished good based on available columns. */
  isFinishedGoodErrorRow(row: any, itemTypes: ItemTypeDTO[]): boolean {
    if (!row) return false;
    const val = (k: string) => row[k] !== undefined && row[k] !== null && row[k] !== '';
    // Product Type column is authoritative when it resolves against the live itemTypes list.
    const productTypeRaw = String(row?.producttype || '').trim();
    if (productTypeRaw) {
      const matchedType = itemTypes.find(t => t.typeName?.toLowerCase() === productTypeRaw.toLowerCase());
      if (matchedType) {
        return isProduct({ itemTypeID: matchedType.itemTypeID });
      }
    }
    // Finished goods template produces 'sellingprice'; also respect optional type flags
    return val('sellingprice')
      || String(row?.itemtype || '').toLowerCase() === 'finished'
      || String(row?.itemtypeid || '').toLowerCase() === '2';
  }

  /** Treat any non-finished-good error row as raw material. */
  isRawMaterialErrorRow(row: any, itemTypes: ItemTypeDTO[]): boolean {
    if (!row) return false;
    return !this.isFinishedGoodErrorRow(row, itemTypes);
  }

  hasFinishedGoodErrors(errors: ImportRowError[], itemTypes: ItemTypeDTO[]): boolean {
    return this.getFailedRows(errors).some(row => this.isFinishedGoodErrorRow(row, itemTypes));
  }

  hasRawMaterialErrors(errors: ImportRowError[], itemTypes: ItemTypeDTO[]): boolean {
    return this.getFailedRows(errors).some(row => this.isRawMaterialErrorRow(row, itemTypes));
  }

  async downloadFailedFinishedGoods(
    errors: ImportRowError[],
    context: { categories: CategoryDTO[]; uomList: UnitOfMeasureDTO[]; itemTypes: ItemTypeDTO[] }
  ): Promise<{ exported: boolean }> {
    const failedFG = this.getFailedRows(errors).filter(row => this.isFinishedGoodErrorRow(row, context.itemTypes));
    if (!failedFG.length) {
      return { exported: false };
    }

    const dataRows = failedFG.map(row => ({
      itemName: String(row['itemname*'] || row['itemname'] || '').trim(),
      itemCode: String(row['sku'] || row['itemcode'] || '').trim(),
      category: String(row['category'] || '').trim(),
      uom: String(row['unitofmeasure'] || row['uom'] || '').trim(),
      productType: String(row['producttype'] || '').trim(),
      sellingPrice: row['sellingprice'] ?? null
    }));
    const categories = context.categories.map(c => c.categoryName);
    const uoms = context.uomList.map(u => u.uomCode);
    const itemTypeNames = context.itemTypes.map(t => t.typeName).filter(t => t !== 'Raw Material');
    await this.excelService.generateProductsTemplate(categories, uoms, dataRows, itemTypeNames);
    return { exported: true };
  }

  async downloadFailedRawMaterials(
    errors: ImportRowError[],
    context: { categories: CategoryDTO[]; uomList: UnitOfMeasureDTO[]; suppliers: SupplierDTO[]; itemTypes: ItemTypeDTO[] }
  ): Promise<{ exported: boolean }> {
    const failedRM = this.getFailedRows(errors).filter(row =>
      this.isRawMaterialErrorRow(row, context.itemTypes) && !this.isFinishedGoodErrorRow(row, context.itemTypes)
    );
    if (!failedRM.length) {
      return { exported: false };
    }

    const dataRows = failedRM.map(row => ({
      itemName: String(row['itemname*'] || row['itemname'] || '').trim(),
      itemCode: String(row['sku'] || row['itemcode'] || '').trim(),
      description: String(row['description'] || '').trim(),
      category: String(row['category'] || '').trim(),
      uom: String(row['unitofmeasure'] || row['uom'] || '').trim(),
      supplier: String(row['supplier'] || '').trim(),
      purchasePrice: row['purchaseprice'] ?? null,
      unitCost: row['unitcost'] ?? null,
      purchaseQuantity: row['purchasequantity'] ?? null,
      lowStockThreshold: row['lowstockthreshold'] ?? null
    }));
    const categories = context.categories.map(c => c.categoryName);
    const uoms = context.uomList.map(u => u.uomCode);
    const suppliers = context.suppliers.map(s => s.companyName);
    await this.excelService.generateInventoryTemplate(categories, uoms, suppliers, dataRows);
    return { exported: true };
  }
}
